export interface DynamicEvents {
  'data-table-refetch': null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'data-table-row-updated': any;
}

type ExtractDynamicEventType<T extends string> = T extends `event:${infer Type}:${string}` ? Type : never;

type DynamicEventPayload<T extends string> =
  ExtractDynamicEventType<T> extends keyof DynamicEvents ? DynamicEvents[ExtractDynamicEventType<T>] : never;

// Augmentable static events map.
// DO NOT put anything in here in the base library.
// Feature libraries will `declare module`-augment this interface.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
export interface AppEvents {}

export type StaticEventName = keyof AppEvents;
export type DynamicEventName = `event:${keyof DynamicEvents}:${string}`;
export type EventName = StaticEventName | DynamicEventName;

type PayloadOf<T extends EventName> = T extends keyof AppEvents ? AppEvents[T] : T extends `event:${string}` ? DynamicEventPayload<T> : never;

export class EventSystem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static eventQueues: Record<string, ((event: any) => void)[]> = {};

  public static listen<T extends EventName>(event: T, listener: (payload: PayloadOf<T>) => void): void {
    if (!this.eventQueues[event]) {
      this.eventQueues[event] = [];
    }

    this.eventQueues[event].push(listener as (e: unknown) => void);
  }

  public static stopListening<T extends EventName>(event: T, listener: (payload: PayloadOf<T>) => void): void {
    const q = this.eventQueues[event];
    if (!q) return;
    this.eventQueues[event] = q.filter((x) => x !== (listener as (e: unknown) => void));
  }

  public static fireEvent<T extends EventName>(event: T, data: PayloadOf<T>): void {
    if (import.meta.hot) {
      console.debug(
        new Error().stack?.split('\n')[2]?.trim().split(' ')[1],
        'fired event',
        event,
        'to',
        this.eventQueues[event]?.length ?? 0,
        'listeners. data:',
        data,
      );
    }

    const q = this.eventQueues[event];
    if (!q) return;
    for (const listener of q) listener(data);
  }

  // Helper to build dynamic event names safely
  public static createDynamicEventName<T extends keyof DynamicEvents>(type: T, id: string): `event:${T}:${string}` {
    return `event:${type}:${id}` as const;
  }
}
